/**
 * React Query hooks for WS-4 (fieldiq-api#1294): the Well Servicing rate card,
 * a rent-in's billing rate, and field tickets — the Well Testing hooks
 * (useWtTickets.ts) carried over, since this vertical's API mirrors WT-4.
 *
 * Same rules as useWellServicing.ts: every key starts
 * ['wellServicing'] and every write invalidates that one prefix (a line
 * moves the ticket, the register's total and the job's Tickets tab); every
 * read spreads LOAD_FAILURE_QUERY_OPTIONS so a refusal reads as a refusal,
 * never as an empty list.
 *
 * A write that can return a signed ticket to draft resolves to
 * `{ ticket, reverted }` so the screen can say so — `reverted_to_draft` is
 * never dropped on the way.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrapArray } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { putToPresignedUrl } from '../api/upload';
import { invalidateWs, unwrapWs, unwrapWsList } from './useWellServicing';
import type {
  WsRate,
  WsRateWrite,
  WsRentInBilling,
  WsTicket,
  WsTicketEdit,
  WsTicketHeaderWrite,
  WsTicketLineCreate,
  WsTicketLinePatch,
  WsTicketRecipient,
  WsTicketSendResult,
} from '../types/ws-tickets';

type Params = Record<string, string | undefined>;
const clean = (p?: Params) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  Object.entries(p).forEach(([k, v]) => {
    if (v) out[k] = v;
  });
  return Object.keys(out).length ? out : undefined;
};

export const wsTicketKeys = {
  rates: (p?: Params) => ['wellServicing', 'rates', clean(p) ?? {}] as const,
  rate: (id: string) => ['wellServicing', 'rate', id] as const,
  rentInBilling: (id: string) => ['wellServicing', 'rentInBilling', id] as const,
  tickets: (p?: Params) => ['wellServicing', 'tickets', clean(p) ?? {}] as const,
  ticket: (id: string) => ['wellServicing', 'ticket', id] as const,
  recipient: (id: string) => ['wellServicing', 'ticketRecipient', id] as const,
  equipmentTypes: () => ['wellServicing', 'equipmentTypes'] as const,
};

function useWsWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateWs(qc) });
}

/**
 * A delete: drop the deleted record's own query and do NOT wait on the refetch.
 * Awaiting invalidateWs here refetched the gone record (404, with retries) for
 * ~4s before the screen's onSuccess could navigate away — measured on Pipeline & Facility's PFT-0003.
 */
function useWsDelete(fn: (id: string) => Promise<unknown>, key: (id: string) => readonly unknown[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: key(id), exact: true });
      void invalidateWs(qc);
    },
  });
}

/** A ticket write's echo: the ticket as it now is, and whether it went back to draft. */
export interface WsTicketEditResult {
  ticket: WsTicket | null;
  reverted: boolean;
}
const editResult = (body: WsTicketEdit | unknown): WsTicketEditResult => ({
  ticket: unwrapWs<WsTicket>(body).data,
  reverted: !!(body && typeof body === 'object' && (body as { reverted_to_draft?: unknown }).reverted_to_draft === true),
});

// ─── Rate card ───────────────────────────────────────────────────────────────

export interface WsRateFilters extends Params {
  /** A customer's uuid, or 'default'. */
  customer_id?: string;
  kind?: string;
  on?: string;
}

export function useWsRates(filters?: WsRateFilters, enabled = true) {
  return useQuery({
    queryKey: wsTicketKeys.rates(filters),
    queryFn: async () => unwrapWsList<WsRate>((await api.wellServicing.rates.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsRate(id: string | undefined) {
  return useQuery({
    queryKey: wsTicketKeys.rate(id ?? ''),
    queryFn: async () => unwrapWs<WsRate>((await api.wellServicing.rates.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWsRate = () =>
  useWsWrite(async (body: WsRateWrite) => unwrapWs<WsRate>((await api.wellServicing.rates.create(body)).data));
export const useUpdateWsRate = () =>
  useWsWrite(async ({ id, body }: { id: string; body: WsRateWrite }) =>
    unwrapWs<WsRate>((await api.wellServicing.rates.update(id, body)).data));
export const useDeleteWsRate = () => useWsDelete(async (id: string) => (await api.wellServicing.rates.remove(id)).data, wsTicketKeys.rate);

/** The tenant's equipment types, for an owned-equipment rate. */
export function useWsEquipmentTypes(enabled = true) {
  return useQuery({
    queryKey: wsTicketKeys.equipmentTypes(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null }>((await api.equipmentTypes.list()).data)
        .map((t) => ({ id: t.id, name: t.name || '—' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── A rent-in's billing rate ────────────────────────────────────────────────

export function useWsRentInBillingRates(rentInIds: string[], enabled = true) {
  return useQueries({
    queries: rentInIds.map((id) => ({
      queryKey: wsTicketKeys.rentInBilling(id),
      queryFn: async () => unwrapWs<WsRentInBilling>((await api.wellServicing.rentInBilling.get(id)).data),
      enabled,
      ...LOAD_FAILURE_QUERY_OPTIONS,
    })),
  });
}

export const useSetWsRentInBillingRate = () =>
  useWsWrite(async ({ rentInId, rateId }: { rentInId: string; rateId: string | null }) =>
    unwrapWs<WsRentInBilling>((await api.wellServicing.rentInBilling.set(rentInId, rateId)).data));

// ─── Tickets ─────────────────────────────────────────────────────────────────

export interface WsTicketFilters extends Params {
  job_id?: string;
  customer_id?: string;
  status?: string;
}

export function useWsTickets(filters?: WsTicketFilters, enabled = true) {
  return useQuery({
    queryKey: wsTicketKeys.tickets(filters),
    queryFn: async () => unwrapWsList<WsTicket>((await api.wellServicing.tickets.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsTicket(id: string | undefined) {
  return useQuery({
    queryKey: wsTicketKeys.ticket(id ?? ''),
    queryFn: async () => unwrapWs<WsTicket>((await api.wellServicing.tickets.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Who a send would address — the office's write role only (the API gates it as a write). */
export function useWsTicketRecipient(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: wsTicketKeys.recipient(id ?? ''),
    queryFn: async () => unwrapWs<WsTicketRecipient>((await api.wellServicing.tickets.recipient(id as string)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWsTicket = () =>
  useWsWrite(async ({ jobId, workDate }: { jobId: string; workDate?: string }) =>
    unwrapWs<WsTicket>((await api.wellServicing.tickets.create(jobId, workDate ? { work_date: workDate } : {})).data));

/** Refresh answers `refreshed.reverted` rather than `reverted_to_draft`; both reach the screen as `reverted`. */
export const useRefreshWsTicket = () =>
  useWsWrite(async (id: string): Promise<WsTicketEditResult> => {
    const body = (await api.wellServicing.tickets.refresh(id)).data as { refreshed?: { reverted?: unknown } } | undefined;
    return { ticket: unwrapWs<WsTicket>(body).data, reverted: body?.refreshed?.reverted === true };
  });

export const useUpdateWsTicket = () =>
  useWsWrite(async ({ id, body }: { id: string; body: WsTicketHeaderWrite }) =>
    editResult((await api.wellServicing.tickets.update(id, body)).data));

export const useDeleteWsTicket = () => useWsDelete(async (id: string) => (await api.wellServicing.tickets.remove(id)).data, wsTicketKeys.ticket);

export const useAddWsTicketLine = () =>
  useWsWrite(async ({ id, body }: { id: string; body: WsTicketLineCreate }) =>
    editResult((await api.wellServicing.tickets.addLine(id, body)).data));

export const useUpdateWsTicketLine = () =>
  useWsWrite(async ({ id, lineId, body }: { id: string; lineId: string; body: WsTicketLinePatch }) =>
    editResult((await api.wellServicing.tickets.updateLine(id, lineId, body)).data));

export const useRemoveWsTicketLine = () =>
  useWsWrite(async ({ id, lineId }: { id: string; lineId: string }) =>
    editResult((await api.wellServicing.tickets.removeLine(id, lineId)).data));

export const useOfficeApproveWsTicket = () =>
  useWsWrite(async (id: string) => unwrapWs<WsTicket>((await api.wellServicing.tickets.officeApprove(id)).data));

export const useSendWsTicket = () =>
  useWsWrite(async ({ id, email, cc }: { id: string; email?: string; cc?: string[] }): Promise<WsTicketSendResult> => {
    const body: { email?: string; cc?: string[] } = {};
    if (email) body.email = email;
    if (cc && cc.length) body.cc = cc;
    return (await api.wellServicing.tickets.send(id, body)).data;
  });

/**
 * Upload the photo of the signed paper ticket through the presigned flow:
 * presign a `ws_ticket` upload against this ticket, PUT the file to storage
 * (putToPresignedUrl throws unless storage answered 2xx), confirm, and return
 * the upload id. No multipart body is built, so no Content-Type is set by hand
 * on an API call. Online only: the offline upload queue has no step four.
 */
export async function uploadWsTicketFile(ticketId: string, file: File): Promise<string> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new Error('You are offline. The signed ticket can only be attached with a connection — try again when you have signal.');
  }
  const contentType = file.type || 'application/octet-stream';
  const presign = await api.uploads.getPresignedUrl({
    fileName: file.name || 'signed-ticket',
    contentType,
    entityType: 'ws_ticket',
    entityId: ticketId,
  });
  // The uploads row is minted at presign (`uploadId`); confirm answers only the size.
  const p = ((presign.data as { data?: unknown })?.data ?? presign.data) as { uploadUrl?: unknown; key?: unknown; uploadId?: unknown };
  if (typeof p?.uploadUrl !== 'string' || typeof p?.key !== 'string' || typeof p?.uploadId !== 'string') {
    throw new Error('The upload could not be prepared. Try again.');
  }
  await putToPresignedUrl(p.uploadUrl, file, contentType);
  await api.uploads.confirm({ key: p.key, size: file.size });
  return p.uploadId;
}

/** Every step throws, so nothing reads as recorded unless the last POST answered 2xx. Once only: 409 `approval_already_recorded`. */
export const useRecordWsTicketApproval = () =>
  useWsWrite(async ({ id, file, approvedByName, approvedOn }: { id: string; file: File; approvedByName: string; approvedOn: string }) => {
    const uploadId = await uploadWsTicketFile(id, file);
    return unwrapWs<WsTicket>(
      (await api.wellServicing.tickets.recordApproval(id, { upload_id: uploadId, approved_by_name: approvedByName, approved_on: approvedOn })).data,
    );
  });

/** Void keeps the ticket on file and stops it billing; the reason is required (400 `reason_required`). */
export const useVoidWsTicket = () =>
  useWsWrite(async ({ id, reason }: { id: string; reason: string }) =>
    unwrapWs<WsTicket>((await api.wellServicing.tickets.void(id, reason)).data));
